import { useEffect, useMemo, useState } from 'react';
import { postEvent } from '../../../../api/tracking';
import { PremiumShop } from '../PremiumShop';

import { PremiumInfoState } from '../../../../store/premium/reducer';
import { LoginState } from '../../../../store/login/reducer';
import { TipsterState } from '../../../../store/tipster/reducer';
import { PremiumProduct } from '../interfaces/PremiumProduct';
import { usePaymentStageContext } from '../../../PaymentGateway/__providers__/PaymentStageContext';
import { useAnalyticsHandlers } from '../../../../analytics/__providers__/AnalyticsHandlersProvider';

interface usePremiumProductProps {
  requestPublicPremiumInfo: (tipster: string) => void;
  getTipsterInfo: (tipster_id: string) => any;
  premiumInfoState: PremiumInfoState;
  loginState: LoginState;
  tipsterState: TipsterState;
  tipsterID: string;
}

export const usePremiumProduct = ({
  premiumInfoState,
  loginState,
  tipsterState,
  requestPublicPremiumInfo,
  getTipsterInfo,
  tipsterID,
}: usePremiumProductProps) => {
  const [shop, setShop] = useState<PremiumShop | null>(null);
  const [isUnavailable, setIsUnavailable] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const { selectedProductNum } = usePaymentStageContext();
  const analytics = useAnalyticsHandlers();
  const [isTracked, setIsTracked] = useState<boolean>(false);

  const areStatesLoading = useMemo(() => {
    const isDoneLoading =
      premiumInfoState.requested &&
      !premiumInfoState.isLoading &&
      premiumInfoState.data &&
      (!!premiumInfoState.data.length || premiumInfoState.error) &&
      loginState.requested &&
      !loginState.isLoading &&
      tipsterState.data &&
      !tipsterState.isLoading;

    return !isDoneLoading;
  }, [premiumInfoState, loginState, tipsterState]);

  useEffect(() => {
    getTipsterInfo(tipsterID);
    requestPublicPremiumInfo(tipsterID);
    loginState.user &&
      postEvent('WEB_PREMIUM_PURCHASE_VIEWED', loginState.user.user.id, {
        tipster_id: tipsterID,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!areStatesLoading) {
      if (premiumInfoState.error) {
        setIsUnavailable(true);
        setIsLoading(false);
      } else {
        try {
          const { data: premiumShop } = premiumInfoState;
          const { tipster: tipsterData } = tipsterState.data;

          if (loginState.user && !isTracked) {
            analytics?.client.Mixpanel.log('WEB_PREMIUM_PURCHASE_VIEWED', [
              {
                tipster_id: tipsterData.id,
                tipster_name: tipsterData.name,
              },
            ]);
          }
          setShop(
            new PremiumShop(premiumShop, tipsterData, selectedProductNum),
          );
        } catch (e: any) {
          setIsUnavailable(true);
          setIsLoading(false);
        }
      }
    }
    setIsTracked(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areStatesLoading]);
  useEffect(() => {
    shop && setIsLoading(false);
  }, [shop]);

  const handleSelectProduct = (product: PremiumProduct) => {
    shop && shop.handleSelectProduct(product);
    setReloadCount(reloadCount + 1);
  };

  return {
    isLoading,
    shop,
    isUnavailable,
    handleSelectProduct,
  };
};
